import React, { useCallback, useMemo, useState } from 'react';
import { Button, Menu, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import { isEmpty } from 'lodash';
import { ProgressButton } from 'modules/common/components';
import {
  TimeSignal,
  TimeSignalAttribute,
  TimeSignalClassification
} from 'graphql/types';
import { menuItems, menuProps, useMenuStyles } from 'modules/notes/common';
import NoteActionMenutItem from 'modules/notes/common/components/NoteFooter/NoteActionMenutItem';
import { useDialogState } from 'modules/common/hooks';
import { useSaveSplitNote } from '../hooks';
import { OFFSET_ENTRY, SPLIT_NOTE_FIELDS } from '../types';
import { useSplitEntriesContext } from '../SplitEntriesProvider';
import { CancelSplitDialog } from './CancelSplitDialog';

const useStyles = makeStyles((theme) => ({
  splitNote: {
    fontWeight: 600
  },
  cancel: {
    marginRight: theme.spacing(1)
  },
  actions: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(2, 0)
  }
}));

type SplitNoteFooterProps = {
  onNoteDiscard: () => void;
  note: TimeSignal;
  attributes?: TimeSignalAttribute[];
};

export const SplitNoteFooter: React.FC<SplitNoteFooterProps> = ({
  onNoteDiscard,
  note,
  attributes
}: SplitNoteFooterProps) => {
  const classes = useStyles();
  const menuClasses = useMenuStyles();
  const {
    formState: { errors },
    formState,
    setValue
  } = useFormContext();
  const {
    open: cancelNoteOpen,
    closeDialog: cancelCloseDialog,
    openDialog: cancelOpenDialog
  } = useDialogState();

  const onCancel = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (formState.isDirty) {
        cancelOpenDialog();
      } else {
        onNoteDiscard();
      }
    },
    [cancelOpenDialog, formState.isDirty, onNoteDiscard]
  );

  const { loading, saveSplitNote } = useSaveSplitNote({
    note,
    onComplete: onNoteDiscard,
    attributes
  });

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) =>
      setAnchorEl(anchorEl ? null : event.currentTarget),
    [anchorEl]
  );
  const onCloseMenu = useCallback(() => setAnchorEl(null), [setAnchorEl]);
  const onSaveSplitNote = useCallback(
    (classification: TimeSignalClassification) => {
      setValue(SPLIT_NOTE_FIELDS.CLASSIFICATION, classification);
      onCloseMenu();
      saveSplitNote();
    },
    [onCloseMenu, saveSplitNote, setValue]
  );
  const { splitEntries, isFormError } = useSplitEntriesContext();
  const hasOnlyOffset = useMemo(
    () => splitEntries.length === 1 && splitEntries[0].id === OFFSET_ENTRY,
    [splitEntries]
  );

  return (
    <div className={classes.actions}>
      <Button
        data-testid="split-note-cancel-btn"
        variant="text"
        color="secondary"
        className={classes.cancel}
        onClick={onCancel}
        disabled={loading}
      >
        <Typography color={'textSecondary'}>
          <FormattedMessage id="note.cancel" />
        </Typography>
      </Button>
      <ProgressButton
        data-testid="split-note-submit-btn"
        variant="contained"
        color="primary"
        disabled={!isEmpty(errors) || loading || hasOnlyOffset || isFormError}
        className={classes.splitNote}
        loading={loading}
        onClick={handleClick}
      >
        <FormattedMessage id="note.splitNote" />
      </ProgressButton>
      <Menu
        elevation={0}
        data-testid="split-note-actions-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseMenu}
        classes={menuClasses}
        {...menuProps}
      >
        {menuItems.map(
          ({ title, icon, classification: itemClassification }) => (
            <NoteActionMenutItem
              key={title}
              title={title}
              icon={icon}
              classification={itemClassification}
              selectedClassification={note.classification}
              onSave={onSaveSplitNote}
            />
          )
        )}
      </Menu>
      {cancelNoteOpen && (
        <CancelSplitDialog
          open={cancelNoteOpen}
          closeHandler={cancelCloseDialog}
          cancelSplit={onNoteDiscard}
        />
      )}
    </div>
  );
};
